import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isProvider, useAllProfiles } from '@/features/profile';
import { roleLabel } from '@/shared/lib/labels';
import { USER_ROLES, type UserRole } from '@/shared/model/enums';
import { Async } from '@/shared/ui/Async';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Chip } from '@/shared/ui/Chip';
import { Stars } from '@/shared/ui/Stars';
import { TextField } from '@/shared/ui/TextField';

import { useSetCertifier, useSetVerified } from '../hooks/use-admin';

type SortKey = 'created' | 'rating' | 'reliability';

/** Admin: searchable browser of every profile (D-013). Read-only. */
export function AdminUsersRoute(): JSX.Element {
  const { t } = useTranslation();
  const query = useAllProfiles();
  const setVerified = useSetVerified();
  const setCertifier = useSetCertifier();
  const [search, setSearch] = useState('');
  const [roleFilters, setRoleFilters] = useState<Set<UserRole>>(new Set());
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [certifiersOnly, setCertifiersOnly] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('created');

  const filtered = useMemo(() => {
    const items = query.data ?? [];
    const q = search.trim().toLowerCase();
    const result = items.filter((p) => {
      if (roleFilters.size > 0 && !roleFilters.has(p.role)) return false;
      if (verifiedOnly && !p.verified) return false;
      if (certifiersOnly && !p.isCertifier) return false;
      if (q.length > 0) {
        const hay = `${p.fullName} ${p.city ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    // Cast to a mutable array for sorting (source is readonly).
    return [...result].sort((a, b) => {
      switch (sortKey) {
        case 'rating':
          return b.ratingAvg - a.ratingAvg;
        case 'reliability':
          return b.reliabilityScore - a.reliabilityScore;
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
  }, [query.data, search, roleFilters, verifiedOnly, certifiersOnly, sortKey]);

  function toggleRole(role: UserRole): void {
    setRoleFilters((prev) => {
      const next = new Set(prev);
      if (!next.delete(role)) next.add(role);
      return next;
    });
  }

  return (
    <main className="page stack">
      <h1>{t('admin.usersTitle')}</h1>

      <TextField
        label={t('admin.searchLabel')}
        placeholder={t('admin.searchHint')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="wrap">
        {USER_ROLES.map((role) => (
          <Chip
            key={role}
            label={roleLabel(t, role)}
            selected={roleFilters.has(role)}
            onToggle={() => toggleRole(role)}
          />
        ))}
        <Chip
          label={t('profile.verified')}
          selected={verifiedOnly}
          onToggle={() => setVerifiedOnly((v) => !v)}
        />
        <Chip
          label={t('admin.certifierBadge')}
          selected={certifiersOnly}
          onToggle={() => setCertifiersOnly((v) => !v)}
        />
      </div>

      <div className="wrap">
        <Chip
          label={t('admin.sortNewest')}
          selected={sortKey === 'created'}
          onToggle={() => setSortKey('created')}
        />
        <Chip
          label={t('admin.sortRating')}
          selected={sortKey === 'rating'}
          onToggle={() => setSortKey('rating')}
        />
        <Chip
          label={t('admin.sortReliability')}
          selected={sortKey === 'reliability'}
          onToggle={() => setSortKey('reliability')}
        />
      </div>

      <Async
        query={query}
        isEmpty={() => filtered.length === 0}
        emptyMessage={t('admin.noUsersMatch')}
      >
        {() => (
          <div className="stack">
            {filtered.map((p) => (
              <div key={p.id} className="card stack">
                <div className="row row--between">
                  <div>
                    <strong>{p.fullName}</strong>
                    <p className="muted">
                      {roleLabel(t, p.role)}
                      {p.city ? ` · ${p.city}` : ''}
                    </p>
                  </div>
                  <div className="wrap">
                    {p.verified ? (
                      <Badge label={t('profile.verified')} color="var(--color-success)" />
                    ) : null}
                    {p.isCertifier ? (
                      <Badge label={t('admin.certifierBadge')} color="var(--color-primary)" />
                    ) : null}
                    {p.isAdmin ? (
                      <Badge label={t('admin.adminBadge')} color="var(--color-accent-text)" />
                    ) : null}
                  </div>
                </div>
                {isProvider(p) ? (
                  <div className="wrap">
                    <Stars value={p.ratingAvg} count={p.ratingCount} />
                    <span className="muted">
                      {t('profile.reliability', {
                        pct: Math.round(p.reliabilityScore * 100),
                      })}
                    </span>
                  </div>
                ) : null}

                {/* Inline moderation actions (D-013). verify/certifier only apply
                    to providers — masjid_admin isn't verified or vouched-for. Ban
                    is intentionally absent (Phase 4 flagged it as not-yet-built). */}
                {isProvider(p) ? (
                  <div className="wrap">
                    <Button
                      variant="outline"
                      loading={setVerified.isPending}
                      onClick={() => setVerified.mutate({ profileId: p.id, verified: !p.verified })}
                    >
                      {p.verified ? t('admin.unverify') : t('admin.verify')}
                    </Button>
                    <Button
                      variant="outline"
                      loading={setCertifier.isPending}
                      onClick={() =>
                        setCertifier.mutate({ profileId: p.id, isCertifier: !p.isCertifier })
                      }
                    >
                      {p.isCertifier ? t('admin.removeCertifier') : t('admin.makeCertifier')}
                    </Button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Async>
    </main>
  );
}
